/* A button invoking the project settings.
 * This button is meant to be part of group of navigation buttons.
 * When the settings are open, this button should highlight itself as "current" (aria-current + visual cue).
 */

import React, { memo } from 'react';
import type { LoadingButtonProps } from '@atlaskit/button';
import SettingsIcon from '@atlaskit/icon/core/migration/settings';
import type { RouteName } from '@atlassian/jira-route-name-types/src/RouteName.tsx';
import type { JiraProject } from '@atlassian/jira-business-entity-common/src/types.tsx';
import type { Project } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { ThemedButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button/ThemedButton.tsx';
import type { NavigationProject } from '@atlassian/jira-router-resources-business-navigation/src/types.tsx';
import { Link } from '@atlassian/react-resource-router';
import type { RouteGroup } from '@atlassian/jira-route-name-types/src/RouteGroup.tsx';
import messages from './messages.tsx';

export type Props = {
	project: NavigationProject | JiraProject | Project | null;
	projectUrlBase: string; // with or without the project key, we handle both cases, see below
	onClick?: LoadingButtonProps['onClick'];
};

const _PROJECT_SETTINGS_ROUTES_GROUPS: Array<RouteGroup> = [
	'project-settings-classic',
	'project-settings-core',
	'project-settings-servicedesk',
	'project-settings-software',
];
export const PROJECT_SETTINGS_ROUTES_GROUPS = new Set<RouteGroup>(_PROJECT_SETTINGS_ROUTES_GROUPS);

const _PROJECT_SETTINGS_ROUTES_NAMES: Array<RouteName> = [
	'project-settings-core-proforma-project-forms',
	'project-settings-core-proforma-project-forms',
	'project-settings-classic-data-classifications',
	'classic-software-details',
	'classic-software-features',
	'project-settings-classic-issue-layout',
	'project-settings-classic-issue-layout',
	'classic-software-link-opsgenie',
	'project-settings-software-proforma-edit-forms',
	'project-settings-software-proforma-project-forms',
	'project-settings-software-toolchain-company-managed',
	'classic-software-notifications-issue-logs',
	'legacy-project-settings-collectors',
	'legacy-project-settings-collectors-add',
	'legacy-project-settings-collectors-help',
	'legacy-project-settings-collectors-view',
	'legacy-project-settings-config',
	'project-settings-classic-data-classifications',
	'legacy-project-settings-delete',
	'legacy-project-settings-dev',
	'legacy-project-settings-edit',
	'legacy-project-settings-fields',
	'legacy-project-settings-fields-associate',
	'legacy-project-settings-issue-layout',
	'legacy-project-settings-issue-security',
	'legacy-project-settings-issue-security-associate',
	'legacy-project-settings-issue-types',
	'legacy-project-settings-issue-types-associate',
	'legacy-project-settings-notifications',
	'legacy-project-settings-notifications-associate',
	'legacy-project-settings-opsgenie',
	'legacy-project-settings-people',
	'legacy-project-settings-permissions',
	'legacy-project-settings-permissions-associate',
	'legacy-project-settings-screens',
	'legacy-project-settings-screens-associate',
	'legacy-project-settings-summary',
	'legacy-project-settings-workflows',
	'legacy-project-settings-workflows-associate',

	'project-settings-automation',
	'project-settings-components',

	'project-settings-core-access',
	'project-settings-core-access-redirect',
	'project-settings-core-approvals',
	'project-settings-core-apps',
	'project-settings-core-apps-fields',
	'project-settings-core-automation',
	'project-settings-core-data-classifications',
	'project-settings-core-redirect',
	'project-settings-core-details',
	'project-settings-core-fields',
	'project-settings-core-forge-apps',
	'project-settings-core-issuetypes',
	'project-settings-core-issuetypes-workflow',
	'project-settings-core-notifications-notification-email',
	'project-settings-core-notifications',
	'project-settings-core-notifications-issue-logs',
	'project-settings-core-proforma-jira-form-builder',
	'project-settings-core-proforma-project-forms',
	'project-settings-software-notifications-issue-logs',
	'project-settings-software-forge-apps',
	'project-settings-software-apps-forge',

	'project-settings-servicedesk-apps-fields',
	'project-settings-servicedesk-chat',
	'project-settings-servicedesk-customer-notifications',
	'project-settings-servicedesk-customer-permissions',
	'project-settings-servicedesk-email',
	'project-settings-servicedesk-details',
	'project-settings-servicedesk-forge-apps',
	'project-settings-servicedesk-notifications',
	'project-settings-servicedesk-notifications-notification-email',
	'project-settings-servicedesk-portal-settings',
	'project-settings-servicedesk-ticket-types-config',
	'project-settings-servicedesk-ticket-type-fields',
	'project-settings-servicedesk-itsm-ticket-types-config',
	'project-settings-servicedesk-itsm-ticket-type-fields',
	'project-settings-servicedesk-notification-email',
	'project-settings-servicedesk-internal-notifications-nextGen',
	'project-settings-servicedesk-unassigned-ticket-types-config',
	'project-settings-servicedesk-unassigned-ticket-type-fields',
	'project-settings-servicedesk-widget',

	'project-settings-software-access',
	'project-settings-software-apps-fields',
	'project-settings-software-automation',
	'project-settings-software-boards-card',
	'project-settings-software-boards-csm',
	'project-settings-software-boards-custom-filters',
	'project-settings-software-boards-redirect',
	'project-settings-software-boards-roadmap',
	'project-settings-software-boards-timeline',
	'project-settings-software-data-classifications',
	'project-settings-software-details',
	'project-settings-software-next-gen-features',
	'project-settings-software-fields',
	'project-settings-software-issuetypes',
	'project-settings-software-notifications-notification-email',
	'project-settings-software-notifications',
	'project-settings-software-link-opsgenie',

	'project-settings-software-toolchain-team-managed',
	'project-settings-versions',

	// Route names without "PROJECT-SETTINGS" in their original ROUTE_NAMES constant
	// from src/packages/platform/utils/constants/src/spa-routes.tsx
	'project-settings-playbooks-edit',
	'project-settings-playbooks-list',
	'project-settings-servicedesk-access',
	'project-settings-servicedesk-access-redirect',
	'project-settings-servicedesk-apps',
	'project-settings-servicedesk-automation-copy-rule',
	'project-settings-servicedesk-automation-create',
	'project-settings-servicedesk-automation-edit-rule',
	'project-settings-servicedesk-automation-redirect-copy',
	'project-settings-servicedesk-automation-redirect-create',
	'project-settings-servicedesk-automation-redirect-edit',
	'project-settings-servicedesk-automation-redirect-summary',
	'project-settings-servicedesk-automation-redirect-viewlog',
	'project-settings-servicedesk-automation-summary',
	'project-settings-servicedesk-automation-view-log',
	'project-settings-servicedesk-change-management',
	'project-settings-servicedesk-channels',
	'project-settings-servicedesk-channels-chat',
	'project-settings-servicedesk-channels-email-next-gen',
	'project-settings-servicedesk-channels-permissions-next-gen',
	'project-settings-servicedesk-channels-portal',
	'project-settings-servicedesk-channels-widget',
	'project-settings-servicedesk-classic-apps',
	'project-settings-servicedesk-data-classifications',
	'project-settings-servicedesk-external-links',
	'project-settings-servicedesk-feedback',
	'project-settings-servicedesk-fields',
	'project-settings-servicedesk-incident-management',
	'project-settings-servicedesk-issuetypes',
	'project-settings-servicedesk-issuetypes-workflow',
	'project-settings-servicedesk-itsm-features',
	'project-settings-servicedesk-journey-configuration',
	'project-settings-servicedesk-journey-configuration-edit',
	'project-settings-servicedesk-journeys',
	'project-settings-servicedesk-kb',
	'project-settings-servicedesk-language',
	'project-settings-servicedesk-language-key',
	'project-settings-servicedesk-notifications-customer-notifications',
	'project-settings-servicedesk-notifications-internal-notifications',
	'project-settings-servicedesk-proforma-jira-form-builder',
	'project-settings-servicedesk-proforma-project-forms',
	'project-settings-servicedesk-redirect',
	'project-settings-servicedesk-virtualagent-ai-answers',
	'project-settings-servicedesk-virtualagent-browse-intents',
	'project-settings-servicedesk-virtualagent-browseconversations',
	'project-settings-servicedesk-virtualagent-performance',
	'project-settings-servicedesk-virtualagent-settings',
	'project-settings-servicedesk-virtualagent-standard-flows',
	'project-settings-servicedesk-virtualagent-view-intent',
	'project-settings-servicedesk-virtualagent-view-standard-flow',
	'project-settings-servicedesk-virtualagentonboarding',
	'project-settings-servicedesk-workflow-scheme-config',
	'project-settings-software-access-redirect',
	'project-settings-software-apps',
	'project-settings-software-issuetypes-workflow',
	'project-settings-software-proforma-jira-form-builder',
	'project-settings-software-redirect',
	'project-settings-customer-service-features',
	'project-settings-servicedesk-overview',
];
export const PROJECT_SETTINGS_ROUTES_NAMES = new Set<RouteName>(_PROJECT_SETTINGS_ROUTES_NAMES);

export function isProjectSettingsRoute(route: ReturnType<typeof useCurrentRoute>): boolean {
	return (
		(route.group && PROJECT_SETTINGS_ROUTES_GROUPS.has(route.group)) ||
		PROJECT_SETTINGS_ROUTES_NAMES.has(route.name)
	);
}

export function getProjectSettingsHref(
	project: Props['project'],
	projectUrlBase: Props['projectUrlBase'],
): string {
	const projectKey = project?.key || '';

	// allow a little flexibility in how the "project URL base" is provided:
	// - we allow having the key already in the base (last segment) or not
	// - we allow having a trailing slash or not
	// Rationale:
	// - Jira Business projects "base URL" comes from a const string without the project key
	// We don't want to bother the caller with these details
	const projectUrlSplit = projectUrlBase.split('/').filter(Boolean);

	if (projectUrlSplit.slice(-1)[0] !== projectKey) {
		projectUrlSplit.push(projectKey);
	}

	return `/${projectUrlSplit.join('/')}/settings`;
}

const ProjectSettingsButton = ({ project, projectUrlBase, onClick }: Props) => {
	const { formatMessage } = useIntl();
	const route = useCurrentRoute();

	const isLoading = project == null || !project?.key;
	const isCurrent = isProjectSettingsRoute(route);
	const projectSettingsUrl = getProjectSettingsHref(project, projectUrlBase);

	return (
		<ThemedButton
			iconBefore={<SettingsIcon spacing="spacious" label="" />}
			href={projectSettingsUrl}
			isLoading={isLoading}
			isSelected={isCurrent}
			aria-current={isCurrent}
			appearance="subtle"
			component={Link}
			onClick={onClick}
		>
			{formatMessage(messages.settings)}
		</ThemedButton>
	);
};

export default memo<Props>(ProjectSettingsButton);
